import loadable from '@loadable/component';

import { getMatchParams, getPageParams } from '@/hooks';
import {
  getBreadcrumbs,
  getCitiesList,
  getContent,
  getCurrencyRatesList,
  getOrganizationBySlug,
  getOrganizationsList,
  getProductSkus,
  getProductsList,
  getRunningOperationPromisesBreadcrumbs,
  getRunningOperationPromisesCities,
  getRunningOperationPromisesContent,
  getRunningOperationPromisesCurrencyRates,
  getRunningOperationPromisesOrganization,
  getRunningOperationPromisesProducts,
} from '@/queries';
import { getCurrentPageType, getOrganizationUrl } from '@/utils';

const HomePage = loadable(() => import('@/components/Pages/Home'));
const OrganizationPage = loadable(
  () => import('@/components/Pages/CompanyPage')
);

const DefaultPage = loadable(() => import('@/components/Pages/DefaultPage'));

export function buildRoutes({ organizationsSlugs }) {
  const routes = [
    ...organizationsSlugs?.flatMap((slug) => {
      const basePath = getOrganizationUrl(slug); // Генерація URL для категорії

      return [
        // Основний маршрут категорії
        {
          id: slug,
          path: `${basePath}`,
          component: OrganizationPage,
          props: {
            loadData: async (dispatch, props) => {
              const {
                intl: { locale },
                location,
              } = props;

              const { pathname } = location;
              const { companySlug, currency } = getMatchParams({ pathname });
              // Запускаємо запит на організацію
              const organizationResponse = await dispatch(
                getOrganizationBySlug.initiate({
                  locale,
                  slug: companySlug,
                })
              );
              const { data } = organizationResponse;
              const params = getPageParams(location);

              try {
                if (data.id) {
                  await Promise.allSettled([
                    dispatch(getOrganizationsList.initiate({ locale })),
                    ...(currency
                      ? [dispatch(getCurrencyRatesList.initiate({ currency }))]
                      : []),
                    dispatch(
                      getProductsList.initiate({
                        locale,
                        pathname: location.pathname,
                        params: {
                          ...params,
                          vendor_id: data.id,
                        },
                      })
                    ),
                    dispatch(getCitiesList.initiate({ locale })),
                    dispatch(getContent.initiate({ locale, pathname })),
                    dispatch(getBreadcrumbs.initiate({ locale, pathname })),
                  ]);

                  await Promise.allSettled([
                    dispatch(getRunningOperationPromisesProducts()),
                    dispatch(getRunningOperationPromisesOrganization()),
                    ...(currency
                      ? [dispatch(getRunningOperationPromisesCurrencyRates())]
                      : []),
                    dispatch(getRunningOperationPromisesCities()),
                    dispatch(getRunningOperationPromisesContent()),
                    dispatch(getRunningOperationPromisesBreadcrumbs()),
                  ]);
                }
              } catch (e) {
                console.error('Organization Page load data:', e);
              }
            },
          },
        },
      ];
    }),
    {
      id: 'homePage',
      path: process.env.APP_ROUTER_BASENAME,
      component: HomePage,
      props: {
        loadData: async (dispatch, props) => {
          const {
            intl: { locale },
            location,
          } = props;

          const { pathname } = location;

          const params = getPageParams(location);

          try {
            await Promise.allSettled([
              dispatch(
                getProductsList.initiate({
                  locale,
                  pathname: location.pathname,
                  params,
                })
              ),
              dispatch(getOrganizationsList.initiate({ locale })),
              ...(params?.currency
                ? [
                    dispatch(
                      getCurrencyRatesList.initiate({
                        currency: params.currency,
                      })
                    ),
                  ]
                : []),
              dispatch(getCitiesList.initiate({ locale })),
              dispatch(getContent.initiate({ locale, pathname })),
              dispatch(getBreadcrumbs.initiate({ locale, pathname })),
            ]);

            await Promise.allSettled([
              dispatch(getRunningOperationPromisesProducts()),
              dispatch(getRunningOperationPromisesOrganization()),
              ...(params?.currency
                ? [dispatch(getRunningOperationPromisesCurrencyRates())]
                : []),
              dispatch(getRunningOperationPromisesCities()),
              dispatch(getRunningOperationPromisesContent()),
              dispatch(getRunningOperationPromisesBreadcrumbs()),
            ]);
          } catch (e) {
            console.error('HomePage load data:', e);
          }
        },
      },
    },
    {
      id: 'defaultPage',
      path: '/*',
      component: DefaultPage, // Використаємо як базовий компонент
      props: {
        loadData: async (dispatch, props) => {
          const {
            intl: { locale },
            location: { pathname },
          } = props;
          const params = getMatchParams({ pathname });
          const pageType = getCurrentPageType({ params });
          const isProductPage = pageType === 'productPage';

          try {
            // Спочатку отримуємо дані з getContent
            const content = await dispatch(
              getContent.initiate({ locale, pathname })
            );

            if (content) {
              const currentParams = {
                ...params,
                ...(content?.data?.filters || {}),
              };

              await Promise.allSettled([
                ...(isProductPage
                  ? [
                      dispatch(
                        getProductSkus.initiate({
                          locale,
                          params: currentParams,
                        })
                      ),
                    ]
                  : [
                      dispatch(
                        getProductsList.initiate({
                          locale,
                          pathname,
                          params: currentParams,
                        })
                      ),
                    ]),
                dispatch(getOrganizationsList.initiate({ locale })),
                dispatch(getBreadcrumbs.initiate({ locale, pathname })),
              ]);

              await Promise.allSettled([
                ...(isProductPage
                  ? [
                      dispatch(getRunningOperationPromisesProducts()),
                      dispatch(getRunningOperationPromisesOrganization()),
                    ]
                  : []),
                dispatch(getRunningOperationPromisesContent()),
                dispatch(getRunningOperationPromisesBreadcrumbs()),
              ]);
            }
          } catch (e) {
            console.error('defaultPage load data:', e);
          }
        },
      },
    },
  ];

  return routes;
}

export default buildRoutes;
